import React, { useCallback, useMemo } from 'react';
import { Box, Hint, HintText, ShimmerBox, Text } from '@common-fe/common-fe';

import { AccentText } from '@/components';
import Link from '@/modules/core/components/Link';
import currentTheme from '@/styles/theme';

import { StatisticCard, StatisticDetailsIcon } from '../../StatisticCard.styles';

export interface UndeliverableMailStatisticProps {
  isLoading?: boolean;
  count: number;
  url: string;
  isDetailsLinkVisible?: boolean;
}
export const UndeliverableMailStatistic: React.FC<UndeliverableMailStatisticProps> = ({
  isLoading,
  count,
  url,
  isDetailsLinkVisible,
}) => {
  const hasIssues = useMemo(() => count > 0, [count]);
  const background = useMemo(() => {
    if (hasIssues) {
      return `${currentTheme.colors.dangerContainer}80`;
    }
    return 'cavas';
  }, [hasIssues]);
  const getWrapper = useCallback(
    (node: React.ReactNode) => {
      if (isDetailsLinkVisible) {
        return (
          <Link testId="dashboard_statistics_undeliverable-mail_details-link" isOpenNewTab to={url}>
            {node}
          </Link>
        );
      }
      return node;
    },
    [isDetailsLinkVisible, url]
  );
  return getWrapper(
    <StatisticCard
      data-testid="dashboard_statistics_undeliverable-mail"
      background="canvas"
      border={{ color: 'border2', size: '1px' }}
      round="container1Round"
      pad="spacing16"
      direction="column"
      gap="spacing12"
      height={{ min: '140px' }}
    >
      <Box direction="row" align="center" justify="between" gap="spacing12" height="20px">
        <Hint
          hintWidth={320}
          hintHeight={120}
          hintElement={
            <Box>
              <HintText>
                <Text data-testid="dashboard_statistics_undeliverable-mail_title" size="medium" weight={700}>
                  Undeliverable Mail
                </Text>
              </HintText>
            </Box>
          }
        >
          <Text
            data-testid="dashboard_statistics_undeliverable-mail_title_hint"
            textAlign="center"
            size="small"
          >
            Number of employees whose addresses require review due to returned mail. No additional mail,
            including checks, will be sent to an employee with a flagged address until the address has been
            reviewed.
          </Text>
        </Hint>

        {isDetailsLinkVisible && (
          <Box justify="center" align="center" width="24px" height="24px">
            <StatisticDetailsIcon
              data-testid="dashboard_statistics_undeliverable-mail_details-icon"
              color="iconDisabled"
            />
          </Box>
        )}
      </Box>
      <Box
        data-testid="dashboard_statistics_undeliverable-mail_value"
        background={background}
        round="container2Round"
        border={{ color: hasIssues ? 'canvas' : 'border2', size: '1px' }}
        pad={{ horizontal: 'spacing12', vertical: 'spacing8' }}
        direction="column"
        justify="center"
        gap="spacing2"
      >
        {isLoading ? (
          <>
            <ShimmerBox
              data-testid="dashboard_statistics_undeliverable-mail_loader"
              round="checkboxRound"
              width="77px"
              height="36px"
            />
            <ShimmerBox round="checkboxRound" width="100%" height="20px" margin={{ top: 'spacing2' }} />
          </>
        ) : (
          <>
            <AccentText
              data-testid="dashboard_statistics_undeliverable-mail_count"
              size="2xl"
              weight={700}
              color="textBody"
            >
              {count}
            </AccentText>
            <Text
              data-testid="dashboard_statistics_undeliverable-mail_label"
              size="medium"
              weight={400}
              color="textBody"
            >
              {count === 1 ? 'Employee' : 'Employees'}
            </Text>
          </>
        )}
      </Box>
    </StatisticCard>
  );
};

export default UndeliverableMailStatistic;
